import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  apiRequest,
  SuccessToast,
  ErrorToast,
  toastConfig,
  saveToken,
  devError,
} from "@ridecloud/shared";
import { Toaster } from "react-hot-toast";
import { LoaderInside } from "../components/Loader"; // Import Loader component
import { Mail, ArrowLeft, ArrowRight, KeyRound } from "lucide-react";
import "../css/ForgotPassword.css";
import "../css/OtpLogin.css";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const [step, setStep] = useState(1); // Track the current step
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", ""]);

  useEffect(() => {
    if (cooldown === 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => {
        if (prev === 1) clearInterval(timer);
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);
  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 3) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      prevInput?.focus();
    }
  };
  const handleSendOTP = async () => {
    if (cooldown > 0) return; // Prevent sending if still in cooldown
    setLoading(true);
    try {
      const res = await apiRequest(async (APIv1) =>
        APIv1.post(
          "/auth/send-otp",
          { email, type: "login" },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
      );

      SuccessToast(res.data.message || "OTP sent successfully!");
      setOtp(["", "", "", ""]);
      setStep(2);
      setCooldown(60); // Start 60-second cooldown
    } catch (err) {
      ErrorToast(err.response?.data?.message || "Failed to send OTP");
      devError("Error sending OTP:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setLoading(true); // Show loader
    try {
      const res = await apiRequest(async (APIv1) =>
        APIv1.post(
          "/auth/verify-otp",
          { email, otp: otp.join(""), type: "login" },
          {
            headers: {
              "Content-Type": "application/json",
            },
          }
        )
      );
      await saveToken(res.data.token);
      setOtp(["", "", "", ""]);

      SuccessToast("Logged in successfully");
      navigate("/dashboard");
    } catch (err) {
      ErrorToast(err.response.data.message || "Invalid email or password");
      devError("Error verifying OTP:", err);
    } finally {
      setLoading(false); // Hide loader
    }
  };

  return (
    <div className="forgot-password-wrapper">
      <div className="forgot-password-container">
        <div
          className="forgot-back-icon-container"
          onClick={() => navigate("/login")}
        >
          <ArrowLeft className="forgot-back-icon" />
        </div>
        <Toaster {...toastConfig} />

        <div className="otp-login-header">
          <img
            src="/img/ride_cloud.png"
            alt="icon"
            className="w-14 h-14 sm:w-14 sm:h-14"
          />

          <h2 className="forgot-password-title">Login with OTP</h2>
          <p className="forgot-password-subtitle">
            Secure, hassle-free login with OTP
          </p>
        </div>

        <form onSubmit={handleVerifyOtp} className="forgot-password-form">
          <div className="forgot-password-combined-section">
            {/* Step 1: Email Verification */}
            {step === 1 && (
              <div className="forgot-password-step-container active">
                <div className="forgot-password-step-number">1</div>
                <div className="forgot-password-step-content">
                  <h3>Email Verification</h3>
                  <p>We'll send you an OTP by email.</p>
                  <div className="forgot-password-input-group">
                    <Mail className="forgot-password-input-icon" />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="Email address"
                      className="forgot-password-input"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    className="forgot-password-section-button"
                    onClick={handleSendOTP}
                    disabled={cooldown > 0 || loading}
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : "Send Code"}
                    {loading ? (
                      <LoaderInside /> // Use Loader component
                    ) : (
                      <>
                        <ArrowRight className="forgot-password-button-icon" />
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: OTP Verification */}
            {step === 2 && (
              <div className="forgot-password-step-container active">
                <div className="forgot-password-step-number">2</div>
                <div className="forgot-password-step-content">
                  <h3>Enter OTP Code</h3>
                  <p>Please enter the OTP sent to your email.</p>

                  <div className="forgot-password-otp-container">
                    {otp.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleKeyDown(index, e)}
                        className="forgot-password-otp-input"
                        required
                      />
                    ))}
                  </div>
                  <div className="forgot-password-button-last">
                    <button
                      type="button"
                      onClick={() => setStep(1)}
                      className="forgot-password-back-button"
                    >
                      <ArrowLeft className="forgot-password-button-icon" />
                      Back
                    </button>
                  </div>
                </div>
              </div>
            )}
            {step === 2 && (
              <button type="submit" className="forgot-password-submit-button">
                Sign In
                {loading ? (
                  <LoaderInside /> // Use Loader component
                ) : (
                  <>
                    <ArrowRight className="forgot-password-button-icon" />
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
